import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import BoardPopup from "@/comm/boardPopup/UpperBoardPopup";
import { useApiUtil } from "@/utils/apiUtil";
import mainVisual from "@/assets/images/otms_main.jpg";

const NOTICE_BOARD_CD = "0001";

const MainPage = ({ openurl }) => {
  const [notices, setNotices] = useState([]);
  const [boardInfo, setBoardInfo] = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { gfnCallApi } = useApiUtil();
  const menuData = useSelector((state) => state.user.menuData);
  const addBtnData = useSelector((state) => state.user.addBtnData);

  useEffect(() => {
    const loadNotices = async () => {
      const [listResult, infoResult] = await Promise.all([
        gfnCallApi({
          method: "POST",
          url: "vocListPaging",
          data: {
            searchStartDt: dayjs()
              .subtract(1, "month")
              .format("YYYY-MM-DD 00:00:00"),
            searchEndDt: dayjs().format("YYYY-MM-DD 23:59:59.999"),
            searchOption: "title",
            searchText: "",
            searchDeptCd: "",
            searchUserId: "",
            blbdCd: NOTICE_BOARD_CD,
            page: 1,
            pageSize: 5,
          },
          showMsg: false,
        }),
        gfnCallApi({
          method: "POST",
          url: "boardList",
          data: { BOARD_CD: NOTICE_BOARD_CD },
          showMsg: false,
        }),
      ]);
      if (listResult?.code >= 0) setNotices(listResult.data?.list || []);
      if (infoResult?.code >= 0) setBoardInfo(infoResult.data || []);
    };
    loadNotices();
  }, []);

  const openNotice = (notice) => {
    setSelectedNotice(notice);
    setIsDetailOpen(true);
  };

  const openNoticeList = () => {
    const noticeMenu = menuData.find((menu) => menu.MENU_NM === "공지사항");
    if (noticeMenu && openurl) {
      const addBtnList = addBtnData.filter(
        (button) => button.MENU_ID === noticeMenu.MENU_ID
      );
      openurl(noticeMenu, addBtnList);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#fff",
        p: { xs: 2, md: "68px 56px" },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1512, mx: "auto" }}>
        <Box
          component="img"
          src={mainVisual}
          alt="농협물류센터 전경"
          sx={{
            display: "block",
            width: "100%",
            minHeight: 210,
            height: "auto",
            objectFit: "cover",
          }}
        />

        <Paper
          variant="outlined"
          square
          sx={{
            mt: 5,
            p: { xs: 2, md: 2.5 },
            boxShadow: "none",
            borderColor: "#e5e7eb",
          }}
        >
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography component="h2" variant="h6" fontWeight={700}>
              공지사항
            </Typography>
            <Button variant="outlined" size="small" onClick={openNoticeList}>
              더보기
            </Button>
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                minWidth: 700,
                "& th, & td": { borderColor: "#e2e6ef", py: 1.05 },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 600,
                      textAlign: "center",
                      borderTop: "2px solid #9aa0aa",
                    },
                  }}
                >
                  <TableCell width={80}>No</TableCell>
                  <TableCell>제목</TableCell>
                  <TableCell width={100}>조회수</TableCell>
                  <TableCell width={110}>작성자</TableCell>
                  <TableCell width={120}>작성부서</TableCell>
                  <TableCell width={130}>등록일자</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notices.length ? notices.map((notice) => (
                  <TableRow
                    key={notice.NOTI_NO ?? notice.ROW_NUM}
                    hover
                    sx={{ cursor: "pointer" }}
                    onDoubleClick={() => openNotice(notice)}
                  >
                    <TableCell align="center">{notice.ROW_NUM}</TableCell>
                    <TableCell sx={{ maxWidth: 0 }}>
                      <Button
                        onClick={() => openNotice(notice)}
                        sx={{
                          display: "block",
                          maxWidth: "100%",
                          p: 0,
                          color: "text.primary",
                          textAlign: "left",
                          textTransform: "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {notice.TITLE}
                      </Button>
                    </TableCell>
                    <TableCell align="center">{notice.INQ_CNT}</TableCell>
                    <TableCell align="center">{notice.INPT_ID}</TableCell>
                    <TableCell align="center">{notice.DEPT_NM}</TableCell>
                    <TableCell align="center">{notice.INPT_DTTM2}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      등록된 공지사항이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
      <BoardPopup
        open={isDetailOpen}
        board={selectedNotice}
        boardInfo={boardInfo}
        boardType="공지사항"
        boardStatus="modity"
        blbdCd={NOTICE_BOARD_CD}
        onClose={() => setIsDetailOpen(false)}
      />
    </Box>
  );
};

export default MainPage;

// 기존 대시보드
// -----------------------------------------------------------------------------
// import React, { useState , useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Stack,
//   Avatar,
//   List,
//   ListItem,
//   ListItemText,
//   Chip,
//   ToggleButton,
//   ToggleButtonGroup,
// } from "@mui/material";
// import {
//   CheckCircle,
//   Error,
//   PauseCircle,
//   Storage,
// } from "@mui/icons-material";
// import {
//   BarChart,
//   PieChart,
//   Pie,
//   Cell,
//   ResponsiveContainer,
//   Legend,
//   Tooltip,
//   XAxis,
//   YAxis,
//   Bar,
// } from "recharts";
// 
// import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// import IconButton from "@mui/material/IconButton";
// import MuiTooltip from "@mui/material/Tooltip";
// import ViewListIcon from "@mui/icons-material/ViewList";
// import DashboardIcon from "@mui/icons-material/Dashboard";
// import RefreshIcon from "@mui/icons-material/Refresh";
// import StorageIcon from "@mui/icons-material/Storage";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import PauseCircleIcon from "@mui/icons-material/PauseCircle";
// import PauseIcon from "@mui/icons-material/Pause";
// import Autorenew from "@mui/icons-material/Autorenew";
// import Delete from "@mui/icons-material/Delete";
// import BugReport from "@mui/icons-material/BugReport";
// import Block from "@mui/icons-material/Block";
// import InboxIcon from "@mui/icons-material/Inbox";
// import AutorenewIcon from "@mui/icons-material/Autorenew";
// 
// import { useSelector } from "react-redux";
// import TitleLabel from "@/comp/TitleLabel";
// import MainPageTable1 from './MainPage_Table1';
// import MainPageTable2 from './MainPage_Table2';
// import MainPageTable3 from './MainPage_Table3';
// import { useApiUtil } from "@/utils/apiUtil";
// import useTabulatorControl from "@/hooks/useTabulatorControl";
// import { useCommon } from "@/utils/common";
// import LayoutGridFix from "@/layouts/LayoutGridFix";
// 
// 
// const STATE_MAP = {
//   created: "created",      // 최초생성됨
//   restarting: "restarting",// 재시작중
//   removing: "removing",    // 삭제중
//   running: "running",      // 실행중
//   exited: "stopped",       // 중지됨
//   paused: "paused",        // 일시중지됨
//   dead: "dead",            // 좀비도커
//   none: "none",            // 컨테이너 없음
// };
// 
// const STATE_LABEL = {
//   running: "실행중",
//   exited: "중지됨",
//   created: "최초생성",
//   restarting: "재시작중",
//   removing: "삭제중",
//   paused: "일시중지",
//   dead: "좀비도커",
//   none: "컨테이너 없음",
// };
// 
// const CHANNEL_PIE_MAP = [
//   { key: "running", label: "실행중" },
//   { key: "stopped", label: "중지됨" },
//   { key: "paused", label: "일시중지됨" },
//   { key: "restarting", label: "재시작중" },
//   { key: "removing", label: "삭제중" },
//   { key: "zombie", label: "좀비도커" },
//   { key: "noContainer", label: "컨테이너 없음" },
//   { key: "created", label: "최초생성됨" },
// ];
// 
// 
// const jobErrors = [
//   {
//     id: "IF-EMA-0888",
//     name: "수주 마감내역 수신",
//     time: "2025-12-12 11:21:12",
//     error: "발주 요청 저장 실패",
//   },
//   {
//     id: "IF-EMA-0888",
//     name: "수주 마감내역 수신",
//     time: "2025-12-12 11:21:12",
//     error: "발주 요청 저장 실패",
//   },  
// ];
// 
// 
// const COLORS = ["#11998e", "#f7971e", "#8e9eab", "#56ccf2", "#ff416c", "#41295a", "#bdc3c7"];
// 
// /* ================= Dashboard ================= */
// const Dashboard = ({openurl}) => {
//   const commonJs = useCommon();
//   const { gfnCallApi } = useApiUtil();
// 
//   const [viewMode, setViewMode] = useState("dashboard");
// 
//   const [channelList, setChannelList] = useState([]);
//   const [selectedChannel, setSelectedChannel] = useState();
// 
//   const [iferrorList, setIferrorList] = useState([]);
//   const [selectedIFError, setSelectedIFError] = useState();
// 
//   const [wkerrorList, setWkerrorList] = useState([]);
//   const [selectedWkerror, setSelectedWkerror] = useState();
// 
//   const [summaryList, setSummaryList] = useState({});
//   const [channelChart, setChannelChart] = useState([]);
//   const [channelStatusDetail, setChannelStatusDetail ] = useState({ RUNNING: [], STOPPED: [], ETC: []});
// 
//   const menuData = useSelector((state) => state.user.menuData);
//   const addBtnData = useSelector((state) => state.user.addBtnData);
// 
//   const [autoRefresh, setAutoRefresh] = useState(false);
//   const autoRefreshRef = useRef(null);  
// 
//   const tableRef = useRef();
// 
//   const initSummary = {
//     total: 0,
//     running: 0,
//     stopped: 0,
//     paused: 0,
//     restarting: 0,
//     deleting: 0,
//     zombie: 0,
//     removing: 0,
//     dead: 0,
//     created: 0,
//     noContainer: 0,
//   };  
// 
//   // 마스터 그리드 참조 및 제어 훅
//   const grd1 = "grd1";
//   const grd2 = "grd2";
//   const grd3 = "grd3";
// 
//   // grid 객체 참조
//   const tabulatorControl = useTabulatorControl();  
// 
//   const formatDateTime = (dateStr) => {
//     if (!dateStr) return "";
// 
//     const d = new Date(dateStr);
// 
//     const yyyy = d.getFullYear();
//     const mm = String(d.getMonth() + 1).padStart(2, "0");
//     const dd = String(d.getDate()).padStart(2, "0");
//     const hh = String(d.getHours()).padStart(2, "0");
//     const mi = String(d.getMinutes()).padStart(2, "0");
//     const ss = String(d.getSeconds()).padStart(2, "0");
// 
//     return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
//   };
// 
//   const handleRowClick1 = (channel) => {
//     setSelectedChannel(channel); // 상세정보에 쓸 정보 저장
//   };
//   const handleRowClick2 = (channel) => {
//     setSelectedIFError(channel); // 상세정보에 쓸 정보 저장
//   };
//   const handleRowClick3 = (channel) => {
//     setSelectedWkerror(channel); // 상세정보에 쓸 정보 저장
//   };  
//   const handleMove = (menuId) => {
//     console.log("handleMove===", menuId);
//     const targetIndex = menuData.findIndex(
//       (item) => item.MENU_ID === menuId
//     );    
//     if(targetIndex>=0){
//       const node = menuData[targetIndex];
//       const addBtnList = addBtnData.filter(item => item.MENU_ID === node.MENU_ID);
//       openurl(node, addBtnList);
//     }
//   };
// 
//   const handleRefresh = () => {
//     searchList();
//   };
// 
//   const toggleAutoRefresh = () => {
//     if (autoRefresh) {
//       clearInterval(autoRefreshRef.current);
//       autoRefreshRef.current = null;
//       setAutoRefresh(false);
//     } else {
//       handleRefresh(); // 즉시 1회 실행
//       autoRefreshRef.current = setInterval(handleRefresh, 60 * 1000);
//       setAutoRefresh(true);
//     }
//   };
// 
// 
//   const handleViewChange = (event, newView) => {
//     if (newView !== null) setViewMode(newView);
//   };
// 
//   // 채널 상태 정보
//   const buildSummary = (channels = []) => {
//     return channels.reduce((acc, channel) => {
//       acc.total += 1;
// 
//       const state = channel.state;
// 
//       switch (state) {
//         case "running":
//           acc.running += 1;
//           break;
//         case "exited":
//           acc.stopped += 1;
//           break;
//         case "paused":
//           acc.paused += 1;
//           break;
//         case "restarting":
//           acc.restarting += 1;
//           break;
//         case "removing":
//           acc.removing += 1;
//           break;
//         case "dead":
//           acc.zombie += 1;
//           break;
//         case "created":
//           acc.created += 1;
//           break;
//         case "none":
//           acc.noContainer += 1;
//           break;
//         default:
//           acc.none += 1;
//           break;
//       }
// 
//       return acc;
//     }, { ...initSummary });
//   };
// 
//   // pie 차트에 표시
//   const buildChannelChart = (summary = {}) => {
//     return CHANNEL_PIE_MAP
//       .map(({ key, label }) => ({
//         name: label,
//         value: summary[key] || 0,
//       }))
//       .filter(item => item.value > 0); 
//   };  
// 
//   // 하단 중앙
//   const buildChannelStatusDetail = (channels = []) => {
//     return channels.reduce(
//       (acc, channel) => {
//         const item = {
//           name: channel.channelNm,
//           state: channel.state,
//           label: STATE_LABEL[channel.state],
//         };
// 
//         switch (channel.state) {
//           case "running":
//             acc.RUNNING.push(item);
//             break;
// 
//           case "exited":
//             acc.STOPPED.push(item);
//             break;
// 
//           default:
//             acc.ETC.push(item);
//             break;
//         }
// 
//         return acc;
//       },
//       {
//         RUNNING: [],
//         STOPPED: [],
//         ETC: [],
//       }
//     );
//   };
// 
//   const searchList = async () => {
//     const result = await gfnCallApi({
//       method: "post",
//       url: "manager/boardList",
//       data: { channelId: "", channelNm: "", interfaceId:"", interfaceNm:"", 
//           ifStatus:"F", jobId: "", jobNm: "", jobStatus: "F"},
//       showMsg: false,
//     });
//     if(!result || result.code < 0) return;
//     
//     const channeldata = result?.data?.channel?.body || [];
//     const interfacedata = result?.data?.interface?.body || [];
//     const workdata = result?.data?.work?.body || [];
// 
//     setChannelList(channeldata);
//     tabulatorControl.renderData(grd1, channeldata);
// 
//     if(channeldata.length > 0) {
//       setSummaryList(buildSummary(channeldata));
//       setChannelStatusDetail(buildChannelStatusDetail(channeldata));
//     } else {
//       setSummaryList(initSummary);
//       setChannelStatusDetail({});
//     }
// 
//     setIferrorList(interfacedata);
//     tabulatorControl.renderData(grd2, interfacedata);    
// 
//     setWkerrorList(workdata);
//     tabulatorControl.renderData(grd3, workdata);    
// 
//   };    
// 
//   useEffect(() => {
//     setChannelChart(buildChannelChart(summaryList));
//   }, [summaryList]);  
// 
// 
//   useEffect(() => {
//       //searchList();
//   }, []);  
// 
//   useEffect(() => {
//     return () => {
//       if (autoRefreshRef.current) {
//         clearInterval(autoRefreshRef.current);
//       }
//     };
//   }, []);  
// 
//   const tableArea1 = (
//     <>
//         <div sx={{ height: "100%" }}>
//           <div style={{marginBottom: '0px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
//             <TitleLabel label="채널 상태" />
//           </div>
//             <MainPageTable1
//               handleMove={handleMove}
//               channelList={channelList}
//               selectedChannel={selectedChannel}
//               setSelectedChannel={setSelectedChannel}
//               handleRowClick={handleRowClick1}
//               setTabulatorRef={tabulatorControl.setTableRef}
//               gridId={grd1}
//             />
//         </div>    
//     </>    
//   );
// 
//   const tableArea2 = (
//     <>
//         <div sx={{ height: "100%" }}>
//           <div style={{marginBottom: '0px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
//             <TitleLabel label="인터페이스 오류" />
//           </div>
//             <MainPageTable2
//               handleMove={handleMove}
//               iferrorList={iferrorList}
//               selectedIFError={selectedIFError}
//               setSelectedIFError={setSelectedIFError}
//               handleRowClick={handleRowClick2}
//               setTabulatorRef={tabulatorControl.setTableRef}
//               gridId={grd2}
//             />
//         </div>    
//     </>    
//   );
//   
//   const tableArea3 = (
//     <>
//         <div sx={{ height: "100%" }}>
//           <div style={{marginBottom: '0px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
//             <TitleLabel label="작업 오류" />
//           </div>
//             <MainPageTable3
//               handleMove={handleMove}
//               wkerrorList={wkerrorList}
//               selectedWkerror={selectedWkerror}
//               setSelectedWkerror={setSelectedWkerror}
//               handleRowClick={handleRowClick3}
//               setTabulatorRef={tabulatorControl.setTableRef}
//               gridId={grd3}
//             />
//         </div>    
//     </>    
//   );  
//   /*
//     리스트
//   */  
//   const ListView = ({ onMove }) => (
//       <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
//         <Box
//           sx={{
//             width: "100%",
//             height: "100%",
//             p: 2,
//             gap: 1,
//             marginTop: "-30px",
//             display: "flex",
//             flexDirection: "column",        
//             backgroundColor: "#fff"
//           }}
//         >
//           {/*  */}
//           <Box sx={{ flex: "0 0 33%", alignContent: "center", backgroundColor: "#f5f5f5" }}>
//             <LayoutGridFix
//               centerArea={tableArea1}
//             />            
//           </Box>  
//           <Box sx={{ flex: "0 0 33%", alignContent: "center", backgroundColor: "#e0e0e0" }}>
//             <LayoutGridFix
//               centerArea={tableArea2}
//             />                 
//           </Box>  
//           <Box sx={{ flex: "0 0 33%", alignContent: "center", backgroundColor: "#ccc" }}>
//             <LayoutGridFix
//               centerArea={tableArea3}
//             />                  
//           </Box>  
//         </Box>
//       </Box>    
//   );
// /*
//   대시보드 - 상단 전체 리스트
// */
//   const DashboardView = ({ onMove }) => {
//     return (
//       <Box sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column" }}>
//         <Box
//           sx={{
//             width: "100%",
//             height: "100%",
//             p: 2,
//             marginTop: "0px",
//             display: "flex",
//             flexDirection: "column",        
//             backgroundColor: "#fff"
//           }}
//         >
//           {/* KPI  justifyContent=space-evenly */}
//           <Box sx={{ flex: "0 0 25%", alignContent: "center" }}>
//             <Grid container spacing={3} sx={{ width: "100%" }} justifyContent="center">
//               <StatusCard
//                 title="전체 채널"
//                 value={summaryList.total}
//                 icon={<Storage />}
//                 gradient="linear-gradient(135deg, #667eea, #764ba2)"
//               />
// 
//               <StatusCard
//                 title="실행중"
//                 value={summaryList.running}
//                 icon={<CheckCircleIcon />}
//                 gradient="linear-gradient(135deg, #11998e, #38ef7d)"
//               />
// 
//               <StatusCard
//                 title="중지됨"
//                 value={summaryList.stopped}
//                 icon={<PauseCircleIcon />}
//                 gradient="linear-gradient(135deg, #f7971e, #ffd200)"
//               />
// 
//               <StatusCard
//                 title="일시중지됨"
//                 value={summaryList.paused}
//                 icon={<PauseIcon />}
//                 gradient="linear-gradient(135deg, #8e9eab, #eef2f3)"
//               />
// 
//               <StatusCard
//                 title="재시작중"
//                 value={summaryList.restarting}
//                 icon={<Autorenew />}
//                 gradient="linear-gradient(135deg, #56ccf2, #2f80ed)"
//               />
// 
//               <StatusCard
//                 title="삭제중"
//                 value={summaryList.deleting}
//                 icon={<Delete />}
//                 gradient="linear-gradient(135deg, #ff416c, #ff4b2b)"
//               />
// 
//               <StatusCard
//                 title="좀비도커"
//                 value={summaryList.zombie}
//                 icon={<BugReport />}
//                 gradient="linear-gradient(135deg, #41295a, #2F0743)"
//               />
// 
//               <StatusCard
//                 title="컨테이너 없음"
//                 value={summaryList.noContainer}
//                 icon={<Block />}
//                 gradient="linear-gradient(135deg, #bdc3c7, #2c3e50)"
//               />
//             </Grid>
//           </Box>
// 
//           {/* 차트 + 오류 */}
//           <Box sx={{ flex: "1 1 auto", mt: 1, minHeight:"300px" }}>
//             <Grid container spacing={2} mt={1} sx={{ width: "100%", height: "100%" }} justifyContent="center">
//               {/* 차트 */}
//               <Grid item size={4} md={8} sx={{ minHeight: 500 }}>
//                 <ChannelStatusPieCard channelChart={channelChart} />
//               </Grid>
// 
//               <Grid item size={4} md={4} sx={{ minHeight: 500 }}>
//                 <ChannelStatusDetailCard channelStatusDetail={channelStatusDetail} onMore={() => handleMove("I000100110")}/>
//               </Grid>
// 
//               {/* 오류 리스트 */}
//               <Grid item size={4} md={4} sx={{ minHeight: 500 }}>
//                 <ErrorUnifiedCard iferrorList={iferrorList} wkerrorList={wkerrorList} formatDateTime={formatDateTime} onMove={handleMove}/>
//               </Grid>
// 
//             </Grid>
//           </Box>
//         </Box>
//       </Box>
//     );
//   };  
// 
//   return (
//     <Box sx={{ width: "100%", height: "100%", p: 1, display: "flex", flexDirection: "column", 
//           backgroundColor: "#fff" }}>
//       
//       {/* 상단 토글 버튼 */}
//       <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0, height: "30px", pr: 2, overflow: "visible", }}>
// 
//         {/* 자동 갱신 버튼 */}
//         <MuiTooltip title={autoRefresh ? "자동 갱신 중지" : "자동 갱신 시작 (1분)"}>
//           <IconButton
//             size="small"
//             onClick={toggleAutoRefresh}
//             sx={{
//               bgcolor: autoRefresh ? "#e3f2fd" : "#f0f0f0",
//               marginRight: 1,
//               "&:hover": { bgcolor: "#d0e7ff" },
//             }}
//           >
//             <AutorenewIcon
//               sx={{
//                 animation: autoRefresh ? "spin 1.2s linear infinite" : "none",
//                 "@keyframes spin": {
//                   from: {
//                     transform: "rotate(0deg)",
//                   },
//                   to: {
//                     transform: "rotate(360deg)",
//                   },
//                 },                
//               }}
//             />
//           </IconButton>
//         </MuiTooltip>
//         
//         {/* 새로고침 아이콘 */}
//         <MuiTooltip title="새로고침">
//           <IconButton
//             size="small"
//             onClick={() => {
//               handleRefresh();
//             }}
//             sx={{
//               bgcolor: "#f0f0f0",
//               marginRight: 2,
//               "&:hover": { bgcolor: "#d0e7ff" },
//             }}
//           >
//             <RefreshIcon />
//           </IconButton>
//         </MuiTooltip>
//                 
//         <ToggleButtonGroup
//           value={viewMode}
//           exclusive
//           onChange={handleViewChange}
//           size="small"
//           sx={{ borderRadius: "20px" }}
//         >
//           <ToggleButton value="dashboard">
//             <DashboardIcon sx={{ mr: 0.5, height: "100%" }} /> 대시보드
//           </ToggleButton>
//           <ToggleButton value="list">
//             <ViewListIcon sx={{ mr: 0.5, height: "100%" }} /> 리스트
//           </ToggleButton>
//         </ToggleButtonGroup>
//       </Box>
// 
//       {/* 화면 표시 
//       {viewMode === "list" ? (
//         <ListView onMove={handleMove} /> // 리스트 화면 컴포넌트
//       ) : (
//         <DashboardView onMove={handleMove} /> // 기존 대시보드 화면
//       )}
//       */}  
// 
//       {/* Dashboard */}
//       <Box sx={{ flex: 1, display: viewMode === "dashboard" ? "block" : "none" }}>
//         <DashboardView onMove={handleMove} />
//       </Box>
// 
//       {/* List */}
//       <Box sx={{ flex: 1, display: viewMode === "list" ? "block" : "none" }}>
//         <ListView onMove={handleMove} />
//       </Box>      
//     </Box>
//   );
// };
// /*
//   대시보드 - 채널 상태 상세 제목 , 더보기
// */
// const CardHeaderWithMore = ({ title, onMore, color = "default" }) => {
//   const iconColor =
//     color === "error"
//       ? "error.main"
//       : color === "warning"
//       ? "warning.main"
//       : "text.secondary";
// 
//   return (
//     <Stack
//       direction="row"
//       alignItems="center"
//       justifyContent="space-between"
//       mb={1}
//     >
//       <Typography
//         variant="h6"
//         color={color === "error" ? "error" : "inherit"}
//       >
//         {title}
//       </Typography>
// 
//       <MuiTooltip title="더보기">
//         <IconButton
//           size="small"
//           onClick={onMore}
//           sx={{
//             color: iconColor,
//             "&:hover": {
//               backgroundColor:
//                 color === "error"
//                   ? "rgba(255,0,0,0.08)"
//                   : "rgba(0,0,0,0.04)",
//             },
//           }}
//         >
//           <OpenInNewIcon fontSize="small" />
//         </IconButton>
//       </MuiTooltip>
//     </Stack>
//   );
// };
// /*
//   대시보드 - 채널 상태 분포 차트
// */
// const ChannelStatusPieCard = ({channelChart}) => (
//   <Card sx={{ height: "100%" }}>
//     <CardContent sx={{ height: "100%" }}>
//       <Typography variant="h6" mb={1}>
//         채널 상태 분포
//       </Typography>
// 
//       <Box sx={{ height: "calc(100% - 32px)" }}>
//         <ResponsiveContainer width="100%" height="100%">
//           <PieChart>
//             <Tooltip
//               formatter={(value, name) => [`${value} 건`, name]}
//             />        
//             <Legend
//               layout="vertical"
//               verticalAlign="middle"
//               align="right"
//               iconType="circle"
//             />                 
//           
//             <Pie
//               data={channelChart}
//               dataKey="value"
//               nameKey="name"
//               innerRadius="55%"
//               outerRadius="75%"
//               paddingAngle={3}
//               label={({ name, percent }) =>
//                 `${name} ${(percent * 100).toFixed(0)}%`
//               }
//               labelLine={false}
//             >
//               {channelChart.map((_, i) => (
//                 <Cell key={i} fill={COLORS[i % COLORS.length]} />
//               ))}
//             </Pie>
//           
//           </PieChart>
//         </ResponsiveContainer>
//       </Box>
//     </CardContent>
//   </Card>  
// );  
// 
// const StatusBarItem = ({ label, state, statusText, color, etc }) => (
//   <Box
//     sx={{
//       position: "relative",
//       height: 32,
//       borderRadius: 6,
//       background: `linear-gradient(90deg, ${color}, ${color}cc)`,
//       boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.2)",
//       display: "flex",
//       alignItems: "center",
//       px: 1.5,
//       color: "#fff",
//       fontSize: 13,
//     }}
//   >
//     {/* 왼쪽 텍스트 */}
//     <Typography
//       variant="body2"
//       sx={{
//         whiteSpace: "nowrap",
//         overflow: "hidden",
//         textOverflow: "ellipsis",
//         fontWeight: 500,
//         display: "flex",
//         alignItems: "center",
//         gap: 0.5,
//       }}
//     >
//       <span>{label}</span>
// 
//       {/* ETC 상태 표시 */}
//       {etc && state && (
//         <span
//           style={{
//             color: "#fde68a", // ⭐ 강조 색 (노랑계열)
//             fontWeight: 600,
//             fontSize: 12,
//           }}
//         >
//           [{state}]
//         </span>
//       )}      
//     </Typography>
// 
//     {/* 오른쪽 상태 */}
//     <Box sx={{ flexGrow: 1 }} />
// 
//     <Typography
//       variant="caption"
//       sx={{
//         opacity: 0.9,
//         fontWeight: 600,
//       }}
//     >
//       {statusText}
//     </Typography>
//   </Box>
// );
// 
// /*
//   대시보드 - item 채널 상태
// */
// const StatusSection = ({ title, color, barColor, items, etc }) => (
//   <Box>
//     <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
//       <Chip label={title} color={color} size="small" />
//     </Stack>
// 
//     <Stack spacing={0.8}>
//       {items.map((item, idx) => (
//         <StatusBarItem
//           key={idx}
//           label={item.name}
//           state={item.label}
//           statusText={title}
//           color={barColor}
//           etc={etc}
//         />
//       ))}
//     </Stack>
//   </Box>
// );
// 
// 
// const StatusSection_ = ({ title, color, barColor, items }) => (
//   <Box>
//     <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
//       <Chip label={title} color={color} size="small" />
//       <Typography variant="caption" color="text.secondary">
//         {items.length}건
//       </Typography>
//     </Stack>
// 
//     <Stack spacing={0.5}>
//       {items.map((item, idx) => (
//         <Box key={idx}>
//           <Typography variant="body2">
//             {item.name}
//           </Typography>
// 
//           {/* 차트처럼 보이기 */}
//           <Box
//             sx={{
//               height: 8,
//               borderRadius: 4,
//               backgroundColor: "#eee",
//               overflow: "hidden",
//             }}
//           >
//             <Box
//               sx={{
//                 height: "100%",
//                 width: `${item.load}%`,
//                 backgroundColor: barColor,
//                 transition: "width 0.4s",
//               }}
//             />
//           </Box>
//         </Box>
//       ))}
//     </Stack>
//   </Box>
// );
// /*
//   대시보드 - 채널 상태
// */
// const ChannelStatusDetailCard = ({ channelStatusDetail, onMore }) => (
//   <Card sx={{ height: "100%" }}>
//     <CardContent sx={{ height: "100%" }}>
//       <CardHeaderWithMore
//         title="채널 상태 상세"
//         onMore={onMore}
//       />
// 
//       <Stack spacing={3}>
// 
//         {/* 실행중 */}
//         <StatusSection
//           title="실행중"
//           color="success"
//           barColor="#43cea2"
//           items={channelStatusDetail.RUNNING}
//           etc={false}
//         />
// 
//         {/* 중지 */}
//         <StatusSection
//           title="중지"
//           color="warning"
//           barColor="#f7971e"
//           items={channelStatusDetail.STOPPED}
//           etc={false}
//         />
// 
//         {/* 오류 */}
//         <StatusSection
//           title="기타"
//           color="error"
//           barColor="#ff4b2b"
//           items={channelStatusDetail.ETC}
//           etc={true}
//         />
// 
//       </Stack>
//     </CardContent>
//   </Card>
// );
// 
// /*
//   대시보드 - 제목 및 더보기
// */
// const ErrorSectionHeader = ({ title, onMore }) => (
//   <Stack
//     direction="row"
//     alignItems="center"
//     justifyContent="space-between"
//     mb={0.5}
//   >
//     <Typography variant="subtitle2" color="error">
//       {title}
//     </Typography>
// 
//     <MuiTooltip title="더보기">
//       <IconButton size="small" onClick={onMore}>
//         <OpenInNewIcon fontSize="small" />
//       </IconButton>
//     </MuiTooltip>
//   </Stack>
// );
// 
// const EmptyState = ({ text }) => (
//   <Box
//     sx={{
//       height: "80%",
//       minHeight: 100,
//       display: "flex",
//       flexDirection: "column",
//       alignItems: "center",
//       justifyContent: "center",
//       color: "#9ca3af",
//       fontSize: 13,
//     }}
//   >
//     <InboxIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.6 }} />
//     {text}
//   </Box>
// );
// 
// /*
//   대시보드 - 오류 영역
// */
// const ErrorUnifiedCard = ({ iferrorList, wkerrorList, formatDateTime, onMove }) => (
//   <Card sx={{ height: "100%" }}>
//     <CardContent sx={{ height: "100%", p: 1 }}>
//       {/*}
//       <Typography variant="h6" color="error" mb={1}>
//         오류 현황
//       </Typography>
//         {*/}
//       <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
//         
//         {/* 인터페이스 오류 */}
//         <Box sx={{ flex: 1, overflow: "auto", mb: 1 }}>
//           <ErrorSectionHeader
//             title="인터페이스 오류"
//             onMore={() => onMove("I000100120")}
//           />
//           {iferrorList && iferrorList.length > 0 ? (
//             <List dense>
//               {iferrorList.map((item, i) => (
//                 <ListItem key={i} divider>
//                   <ListItemText
//                     primary={item.interfaceNm}
//                     secondary={`${formatDateTime(item.ifLastDate)} | ${item.errorDesc ?? ""}`}
//                   />
//                 </ListItem>
//               ))}
//             </List>
//           ) : (
//             <EmptyState text="인터페이스 오류가 없습니다." />
//           )}
//         </Box>
// 
//         {/* 작업 오류 */}
//         <Box sx={{ flex: 1, overflow: "auto" }}>
//           <ErrorSectionHeader
//             title="작업 오류"
//             onMore={() => onMove("I000100130")}
//           />
//           {wkerrorList && wkerrorList.length > 0 ? (
//             <List dense>
//               {wkerrorList.map((item, i) => (
//                 <ListItem key={i} divider>
//                   <ListItemText
//                     primary={item.jobNm}
//                     secondary={`${formatDateTime(item.jobLastDate)} | ${item.errorDesc ?? ""}`}
//                   />
//                 </ListItem>
//               ))}
//             </List>
//           ) : (
//             <EmptyState text="작업 오류가 없습니다." />
//           )}
//         </Box>
// 
//       </Box>
//     </CardContent>
//   </Card>
// );
// 
// /* ================= KPI Card ================= */
// const StatusCard = ({ title, value, icon, gradient, pulse }) => (
//   <Grid item xs={12} sm={6} md={3}>
//     <Card
//       sx={{
//         height: 120,
//         background: gradient,
//         color: "#fff",
//         width: 150,
// 
//         /* ✅ Hover 시 살짝 확대 */
//         transition: "transform 0.25s ease, box-shadow 0.25s ease",
//         "&:hover": {
//           transform: "scale(1.05)",
//           boxShadow: "0 12px 30px rgba(0,0,0,0.3)",
//         },
// 
//         /* ✅ pulse 효과 (조건부) */
//         ...(pulse && {
//           animation: "pulse 1.8s infinite",
//         }),        
//       }}
//     >
//       <CardContent>
//         <Stack direction="row" justifyContent="space-between">
//           <Box sx={{ width: "58%"}}>
//             <Typography sx={{ fontWeight: "bold"}} variant="body2">{title}</Typography>
//             <Typography
//                 variant="h3"
//                 sx={{
//                   textAlign: "right",
//                   width: "100%",
//                   mt: 0.5,
//                 }}
//               >
//                 {value}
//             </Typography>
//           </Box>
//           <Avatar
//             sx={{
//               bgcolor: "rgba(255,255,255,0.2)",
//               width: 30,
//               height: 30,
//             }}
//           >
//             {icon}
//           </Avatar>
//         </Stack>
//       </CardContent>
//     </Card>
//   </Grid>
// );
// 
// export default Dashboard;
